"use client";

import { Icon } from "@ospweb/sdk-ui";
import React, {
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { useMemoizedFn } from "ahooks";
import { cn } from "utils";
import { Popover } from "@components/Popover";

export type SenderAttachmentSourceId =
  "photo" | "camera" | "files" | "second-opinion";

type SenderAttachmentSourceMenuVariant = "app" | "web";

const ATTACHMENT_MENU_ITEMS = [
  { id: "photo" as const, label: "Photo", icon: "image" },
  { id: "camera" as const, label: "Camera", icon: "camera" },
  { id: "files" as const, label: "Files", icon: "download-extension" },
] as const;

/** App / Web 共用：Second opinion */
const SECOND_OPINION_MENU_ITEM = {
  id: "second-opinion" as const,
  label: "Second opinion",
  icon: "ed",
} as const;

/**
 * 菜单顶部的上传 / 连接项（EDG-591，Figma 110225:595520）；不传则菜单与现状一致。
 * label / 事件名在 JSX 里写字面量，供埋点总表生成器静态识别。
 */
export type SenderAttachmentConnectMenu = {
  uploadLabel: string;
  /** 缺省 = 不出连接项（已有 Plaid 连接） */
  connectLabel?: string;
  /** connect_entry_* 公共维度 */
  hasAccountData: string;
  hasPlaidLink: string;
  onUpload: () => void;
  onConnect: () => void;
  /** 本次展开没点顶部两项就收起 */
  onDismiss: () => void;
};

export type SenderAttachmentSourceMenuProps = {
  disabled?: boolean;
  connectMenu?: SenderAttachmentConnectMenu;
  /** App：Photo / Camera / Files；Web：仅 Files；均含 Second opinion */
  variant?: SenderAttachmentSourceMenuVariant;
  /** 已开启 Second opinion 时，菜单项不可再次点击 */
  secondOpinionDisabled?: boolean;
  onPickPhoto: () => void;
  onPickCamera: () => void;
  onPickFiles: () => void;
  onPickSecondOpinion: () => void;
  children: ReactNode;
};

export function SenderAttachmentSourceMenu({
  disabled = false,
  connectMenu,
  variant = "app",
  secondOpinionDisabled = false,
  onPickPhoto,
  onPickCamera,
  onPickFiles,
  onPickSecondOpinion,
  children,
}: SenderAttachmentSourceMenuProps) {
  const [open, setOpen] = useState(false);
  const connectPickedRef = useRef(false);

  const handleOpenChange = useMemoizedFn((next: boolean) => {
    if (disabled) {
      setOpen(false);
      return;
    }
    if (next && !open) {
      connectPickedRef.current = false;
    }
    if (!next && open && connectMenu && !connectPickedRef.current) {
      connectMenu.onDismiss();
    }
    setOpen(next);
  });

  const handleConnectItemClick = useMemoizedFn((action: () => void) => {
    connectPickedRef.current = true;
    setOpen(false);
    action();
  });

  const connectItemClassName = cn(
    "flex h-10 w-full shrink-0 items-center gap-2 overflow-hidden rounded-[6px] p-2 transition-colors",
    "body_medium_m text-content_primary hover:bg-background_white_overlay5",
  );

  const handleItemClick = useMemoizedFn((id: SenderAttachmentSourceId) => {
    if (id === "second-opinion" && secondOpinionDisabled) {
      return;
    }
    setOpen(false);
    if (id === "photo") {
      onPickPhoto();
      return;
    }
    if (id === "camera") {
      onPickCamera();
      return;
    }
    if (id === "second-opinion") {
      onPickSecondOpinion();
      return;
    }
    onPickFiles();
  });

  const visibleMenuItems = useMemo(() => {
    const attachmentItems =
      variant === "web"
        ? ATTACHMENT_MENU_ITEMS.filter((item) => item.id === "files")
        : ATTACHMENT_MENU_ITEMS;
    return [...attachmentItems, SECOND_OPINION_MENU_ITEM];
  }, [variant]);

  const menuContent = (
    <div
      className={cn(
        "flex flex-col items-stretch overflow-hidden rounded-2",
        "border border-border_white_transparent10",
        "bg-background_solid_neutral_primary p-2",
        "shadow-[0px_0px_32px_0px_rgba(0,0,0,0.05)]",
        // EDG-591：带上传 / 连接项时菜单定宽 248（Figma 110225:595520 / 110225:599692）
        connectMenu && "w-[248px]",
      )}
      role="menu"
      {...(connectMenu
        ? {
            "data-or-view": "connect_entry_view",
            "data-or-param-surface": "composer_menu",
            "data-or-param-has_account_data": connectMenu.hasAccountData,
            "data-or-param-has_plaid_link": connectMenu.hasPlaidLink,
          }
        : {})}
    >
      {connectMenu && (
        <>
          <button
            type="button"
            role="menuitem"
            className={connectItemClassName}
            data-openreplay-label="chat_composermenu_upload"
            data-or-event="connect_entry_tap"
            data-or-param-surface="composer_menu"
            data-or-param-item="upload"
            data-or-param-has_account_data={connectMenu.hasAccountData}
            data-or-param-has_plaid_link={connectMenu.hasPlaidLink}
            onClick={() => handleConnectItemClick(connectMenu.onUpload)}
          >
            <Icon
              name="landscape"
              size={16}
              className="shrink-0 text-content_primary"
              aria-hidden
            />
            <span className="whitespace-nowrap">{connectMenu.uploadLabel}</span>
          </button>
          {connectMenu.connectLabel && (
            <button
              type="button"
              role="menuitem"
              className={connectItemClassName}
              data-openreplay-label="chat_composermenu_connect"
              data-or-event="connect_entry_tap"
              data-or-param-surface="composer_menu"
              data-or-param-item="connect"
              data-or-param-has_account_data={connectMenu.hasAccountData}
              data-or-param-has_plaid_link={connectMenu.hasPlaidLink}
              onClick={() => handleConnectItemClick(connectMenu.onConnect)}
            >
              <Icon
                name="link"
                size={16}
                className="shrink-0 text-content_primary"
                aria-hidden
              />
              <span className="whitespace-nowrap">
                {connectMenu.connectLabel}
              </span>
            </button>
          )}
        </>
      )}
      {visibleMenuItems.map((item) => {
        const isSecondOpinionItem = item.id === "second-opinion";
        const isItemDisabled = isSecondOpinionItem && secondOpinionDisabled;

        return (
          <button
            key={item.id}
            type="button"
            role="menuitem"
            disabled={isItemDisabled}
            aria-disabled={isItemDisabled}
            className={cn(
              "flex h-10 w-full shrink-0 items-center gap-2 overflow-hidden rounded-[6px] p-2 transition-colors",
              "body_medium_m text-content_primary",
              !isItemDisabled && "hover:bg-background_white_overlay5",
              isItemDisabled && "cursor-not-allowed opacity-40",
            )}
            onClick={() => handleItemClick(item.id)}
          >
            <Icon
              name={item.icon}
              size={16}
              className="shrink-0 text-content_primary"
              aria-hidden
            />
            <span className="whitespace-nowrap">{item.label}</span>
          </button>
        );
      })}
    </div>
  );

  const trigger = useMemo(() => {
    if (!React.isValidElement(children)) {
      return children;
    }
    return React.cloneElement(
      children as ReactElement<{
        "aria-expanded"?: boolean;
        "aria-haspopup"?: string;
      }>,
      {
        "aria-haspopup": "menu",
        "aria-expanded": disabled ? false : open,
      },
    );
  }, [children, disabled, open]);

  return (
    <Popover
      open={disabled ? false : open}
      onVisibilityChange={handleOpenChange}
      trigger="click"
      placement="bottom-start"
      content={menuContent}
      contentClassName="border-0 bg-transparent p-0 shadow-none"
      className="inline-flex"
    >
      {trigger}
    </Popover>
  );
}
